/**
 * The ONE tile <-> pixel convention for the arena.
 *
 * There used to be two, and they disagreed by nearly a whole tile. A hero's
 * position point is the sprite anchor, but collision samples the FEET, which
 * render `footOffset` pixels ABOVE that point (map/player.ts `solidAt`). Spawning
 * honoured the offset; path waypoints did not — arena-match mapped a tile CENTRE
 * from the pathfinder straight to `p.y * TILE`, so every hero walked its route one
 * tile too high in Y relative to the grid the route was planned on. That is what
 * jammed fighters against the top border and along the lava seams, and what made
 * the safe-zone and strike-range tests disagree with what was on screen.
 *
 * Every conversion in the arena now goes through these two functions, so the bug
 * cannot come back one call site at a time. Both are pure and take the anchor
 * explicitly rather than importing TILE/FOOT_OFFSET, so they are testable without
 * pulling pixi into the test.
 */

export interface FootAnchor {
  /** World pixels per tile. */
  tile: number;
  /** Pixels the feet sit ABOVE the sprite's position point. */
  footOffset: number;
}

/** World pixels for a hero whose FEET should land on tile coordinate (tx, ty). */
export function pxOfTile(tx: number, ty: number, a: FootAnchor): { x: number; y: number } {
  return { x: tx * a.tile, y: ty * a.tile + a.footOffset };
}

/** The tile coordinate a hero's FEET are standing on, from its world pixels. */
export function tileOfPx(x: number, y: number, a: FootAnchor): { tx: number; ty: number } {
  return { tx: x / a.tile, ty: (y - a.footOffset) / a.tile };
}

/** World pixels that put the feet in the CENTRE of tile (col, row) — the spawn form. */
export function pxOfTileCenter(col: number, row: number, a: FootAnchor): { x: number; y: number } {
  return pxOfTile(col + 0.5, row + 0.5, a);
}
